import { useState, type ReactNode } from 'react'

import { useHealthSignal } from '@krp/client-runtime'

import type { SaleActions, AqsiSaleVm } from '../../sale/model/types'
import type { CheckoutTill, ShiftMark } from '@krp/sale-ui'
import { SaleScreen } from '@krp/sale-ui'
import { QueryState } from '@krp/sale-ui'
import { CABINET_RELOAD_ACTION, REGISTER_REFRESH_ACTION } from '../model/healthTexts'
import { rmkStageOf } from '../model/stage'
import type { RmkActions, RmkVm } from '../model/types'
import { OpeningScreen } from './flow/OpeningScreen'
import { OtherTabScreen } from './flow/OtherTabScreen'
import { StartScreen } from './flow/StartScreen'
import { RmkActionsDialog } from './RmkActionsDialog'
import { ShiftGate } from './ShiftGate'
import { CloseShiftDialog } from './CloseShiftDialog'
import './rmk.css'

/**
 * РМК — одно окно пакета вида на адресе источника (UI-RMK-FLOW, RP-FB22): «Начало работы» (кассир → код → вид цены →
 * касса) → ожидание открытия смены → порог смены → экран продажи. Своя работа определяет этап (её читает наша база,
 * не кабинет); F5 — тот же этап (его решают ответы сервера, а не память вкладки).
 *
 * Связь (RP-UI-LINK, шаг 4): экраны РМК о ней не пишут — окно сообщает сигналы кабинета и кассы в точку «Связь» шапки. Своя работа есть —
 * кабинет и моя касса по `pos/registers/{id}/link`; нет — кабинет по чтению списка касс и выбранная касса. «Актуализировать состояние
 * кассы» из мини-окна точки — одна служебная проверка кассы и новое чтение смены, как прежде в диалоге «Связь».
 */
export function RmkWorkspace({
  rmk,
  rmkActions,
  sale,
  saleActions,
  backgroundWork,
}: {
  rmk: RmkVm
  rmkActions: RmkActions
  sale: AqsiSaleVm
  saleActions: SaleActions
  backgroundWork?: ReactNode
}) {
  // Помним вход в продажу при временном отказе чтения смены. Подтверждённое
  // закрытие сбрасывает пропуск, чтобы после завершения оплаты вернуться к смене.
  const [passed, setPassed] = useState<string | null>(null)
  const [actionsOpen, setActionsOpen] = useState(false)
  const workKey = rmk.mine?.sessionId ?? null
  const stage = rmkStageOf(rmk, sale, workKey !== null && passed === workKey)
  if (workKey !== null && (rmk.shift?.isOpen === false || rmk.shiftCommands.canAdopt) && passed === workKey) setPassed(null)
  else if (stage === 'sale' && workKey !== null && passed !== workKey && rmk.shift?.isOpen === true) setPassed(workKey)
  // «Действия» живут только на экране продажи: ушли с него (закончили работу) — окно не воскресает при возврате.
  if (stage !== 'sale' && actionsOpen) setActionsOpen(false)

  const mine = rmk.mine
  const selected = rmk.start.registers.selected
  const healthDevice = mine?.deviceId ?? selected?.deviceId ?? null
  const onHealthAction = (key: string) => {
    if (key === CABINET_RELOAD_ACTION) {
      rmkActions.reload()
      return
    }
    if (key !== REGISTER_REFRESH_ACTION || healthDevice === null || rmk.busy) return
    rmkActions.checkLink(healthDevice)
    if (mine !== null) rmkActions.readShift()
  }
  useHealthSignal(mine?.health.cabinet ?? rmk.board.cabinet, onHealthAction)
  useHealthSignal(mine?.health.register ?? selected?.health ?? null, onHealthAction)

  const renderStage = () => {
    switch (stage) {
      case 'loading':
        return <QueryState requestIdHidden query={rmk.mineQuery} what="Работа за кассой" emptyText="Касс нет" onRetry={rmkActions.reload} />
      case 'start':
        return <StartScreen vm={rmk} actions={rmkActions} />
      case 'opening':
        return <OpeningScreen vm={rmk} actions={rmkActions} />
      case 'other-tab':
        return rmk.mine === null ? null : <OtherTabScreen mine={rmk.mine} actions={rmkActions} />
      case 'shift':
        return <ShiftGate vm={rmk} actions={rmkActions} hideOperation={rmk.operation?.kind === 'close'} />
      case 'sale': {
        const till: CheckoutTill = {
          backgroundWork,
          // Блок состояния под чеком: смена читается в фоне — одна строка, оплату она не выключает (RP-SALE-FAST, шаг 4); крупный индикатор —
          // только для отказа чтения (с «Повторить») и для отложенной после F5 проверки.
          shiftStatus:
            rmk.shift === null || rmk.shift.isOpen == null ? (
              rmk.shift?.nextCheckAt != null ? (
                <p className="rmk-status__line" role="status">
                  Недавняя проверка уже запрошена. Следующая — в {new Date(rmk.shift.nextCheckAt).toLocaleTimeString('ru-RU')}. Корзину можно набирать.
                </p>
              ) : rmk.shift === null || rmk.shift.query.status === 'loading' || rmk.shift.query.status === 'idle' ? (
                <p className="rmk-status__line" role="status">
                  Состояние смены уточняется. Корзину можно набирать и оплачивать: если смена закрыта, оплата не начнётся.
                </p>
              ) : (
                <QueryState query={rmk.shift.query} what="Состояние смены" emptyText="Проверяем состояние смены" onRetry={rmkActions.reload} />
              )
            ) : undefined,
          shift: shiftMark(rmk),
          where: [mine?.registerName, rmk.source.name, mine?.priceTypeName].filter((part) => part !== undefined && part !== '').join(' · '),
          payBlockedReason: shiftBlock(rmk),
          onActions: () => setActionsOpen(true),
        }
        return (
          <>
            <SaleScreen vm={sale} actions={saleActions} till={till} />
            {actionsOpen && (
              <RmkActionsDialog
                rmk={rmk}
                rmkActions={rmkActions}
                sale={sale}
                saleActions={saleActions}
                onClose={() => setActionsOpen(false)}
              />
            )}
          </>
        )
      }
    }
  }
  return <CloseShiftDialog vm={rmk} actions={rmkActions}>{renderStage()}</CloseShiftDialog>
}

/** Оплату не начать по причине смены — словами и с тем, куда идти (порог смены проверяется на входе, здесь — до денег). */
function shiftBlock(rmk: RmkVm): string | null {
  switch (shiftMark(rmk)) {
    case 'closed':
      return 'Смена закрыта — чек не напечатается. Откройте смену: «Действия» → «Смена».'
    case 'unknown':
      // Смена читается в фоне: оплату это не выключает, закрытую смену служба откажет словами до денег.
      return null
    case 'open':
      if (rmk.shiftCommands.canAdopt) return 'Смена открыта на кассе вне программы — примите её: «Действия» → «Смена».'
      return shiftTooLong(rmk.shift?.openedAt ?? null)
  }
}

/** Кассовая смена живёт не больше 24 часов: дольше касса чек не пробьёт (23.10.2026 касса AQSI сама потребовала закрыть смену). */
const SHIFT_LIMIT_MS = 24 * 60 * 60 * 1000

function shiftTooLong(openedAt: string | null): string | null {
  const opened = openedAt === null ? Number.NaN : Date.parse(openedAt)
  if (Number.isNaN(opened) || Date.now() - opened < SHIFT_LIMIT_MS) return null
  const since = new Date(opened).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  return `Смена открыта больше 24 часов (с ${since}) — касса не пробьёт чек. Закройте смену и откройте новую: «Действия» → «Смена».`
}

function shiftMark(rmk: RmkVm): ShiftMark {
  const open = rmk.shift?.isOpen ?? null
  if (open === null) return 'unknown'
  return open ? 'open' : 'closed'
}
